/**
 * Pusher Service for Real-Time Push Notifications
 * 
 * Setup Instructions:
 * 1. Get Pusher credentials from pusher.com
 * 2. Update backend/.env with PUSHER_APP_ID, PUSHER_KEY, PUSHER_SECRET, PUSHER_CLUSTER
 * 3. Restart the backend server
 */

import Pusher from 'pusher-js';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

let pusherInstance = null;
let pusherConfig = null;

// Notification event types
export const NotificationEvents = {
  NEW_MESSAGE: 'new-message',
  TYPING: 'typing',
  MESSAGE_READ: 'message-read',
  NEW_LOAD_OFFER: 'new-load-offer',
  LOAD_OFFER_RESPONSE: 'load-offer-response',
  SIGNATURE_REQUEST: 'signature-request',
  DOCUMENT_SIGNED: 'document-signed',
  ALERT: 'alert'
};

/**
 * Initialize Pusher with configuration from backend
 */
export async function initializePusher() {
  if (pusherInstance) return pusherInstance;
  
  try {
    // Fetch Pusher config from backend
    const response = await fetch(`${API_URL}/api/messaging/pusher-config`);
    const config = await response.json();
    
    pusherConfig = config;
    
    if (!config.enabled) {
      console.log('Pusher is not enabled on the backend');
      return null;
    }
    
    // Initialize Pusher
    pusherInstance = new Pusher(config.key, {
      cluster: config.cluster,
      forceTLS: true
    });
    
    // Log connection state changes
    pusherInstance.connection.bind('connected', () => {
      console.log('Pusher connected');
    });
    
    pusherInstance.connection.bind('error', (err) => {
      console.error('Pusher connection error:', err);
    });
    
    return pusherInstance;
  } catch (error) {
    console.error('Failed to initialize Pusher:', error);
    return null;
  }
}

/**
 * Get the Pusher instance (initialize if needed)
 */
export async function getPusher() {
  if (!pusherInstance) {
    await initializePusher();
  }
  return pusherInstance;
}

/**
 * Check if Pusher is enabled
 */
export function isPusherEnabled() {
  return pusherConfig?.enabled || false;
}

/**
 * Subscribe to a channel
 */
export async function subscribeToChannel(channelName, eventHandlers = {}) {
  const pusher = await getPusher();
  if (!pusher) return null;
  
  const channel = pusher.subscribe(channelName);
  
  // Bind event handlers
  Object.entries(eventHandlers).forEach(([event, handler]) => {
    channel.bind(event, handler);
  });
  
  return channel;
}

/**
 * Subscribe to carrier notifications
 */
export async function subscribeToCarrierNotifications(carrierId, handlers = {}) {
  const channelName = `carrier-${carrierId}`;
  
  const defaultHandlers = {
    [NotificationEvents.NEW_MESSAGE]: (data) => {
      showBrowserNotification('New Message', data.message_preview, data);
    },
    [NotificationEvents.NEW_LOAD_OFFER]: (data) => {
      showBrowserNotification('New Load Offer', data.message, data);
    },
    [NotificationEvents.SIGNATURE_REQUEST]: (data) => {
      showBrowserNotification('Signature Required', data.message, data);
    },
    ...handlers
  };
  
  return subscribeToChannel(channelName, defaultHandlers);
}

/**
 * Subscribe to admin notifications
 */
export async function subscribeToAdminNotifications(handlers = {}) {
  const channelName = 'admin-notifications';
  
  const defaultHandlers = {
    [NotificationEvents.NEW_MESSAGE]: (data) => {
      showBrowserNotification('New Message', data.message_preview, data);
    },
    [NotificationEvents.LOAD_OFFER_RESPONSE]: (data) => {
      showBrowserNotification('Load Offer Response', data.message, data);
    },
    [NotificationEvents.DOCUMENT_SIGNED]: (data) => {
      showBrowserNotification('Document Signed', data.message, data);
    },
    ...handlers
  };
  
  return subscribeToChannel(channelName, defaultHandlers);
}

/**
 * Unsubscribe from a channel
 */
export async function unsubscribeFromChannel(channelName) {
  const pusher = await getPusher();
  if (pusher) {
    pusher.unsubscribe(channelName);
  }
}

/**
 * Show browser notification
 */
function showBrowserNotification(title, body, data = {}) {
  // Check if Notification API is available
  if (typeof Notification === 'undefined') {
    console.log('Browser notifications not supported');
    return;
  }
  
  // Request permission if not granted
  if (Notification.permission === 'default') {
    Notification.requestPermission();
    return;
  }
  
  if (Notification.permission === 'granted') {
    const notification = new Notification(title, {
      body,
      icon: '/logo192.png',
      badge: '/logo192.png',
      tag: data.type || 'default',
      requireInteraction: false
    });
    
    // Auto-close after 5 seconds
    setTimeout(() => notification.close(), 5000);
    
    // Handle click
    notification.onclick = () => {
      window.focus();
      notification.close();
      
      // Navigate based on notification type
      if (data.conversation_id) {
        // Navigate to messages
        window.location.href = '#messages';
      } else if (data.offer_id) {
        // Navigate to load offers
        window.location.href = '#load-offers';
      } else if (data.request_id) {
        // Navigate to signatures
        window.location.href = '#signatures';
      }
    };
  }
}

/**
 * Disconnect Pusher
 */
export function disconnectPusher() {
  if (pusherInstance) {
    pusherInstance.disconnect();
    pusherInstance = null;
  }
}

export default {
  initializePusher,
  getPusher,
  isPusherEnabled,
  subscribeToChannel,
  subscribeToCarrierNotifications,
  subscribeToAdminNotifications,
  unsubscribeFromChannel,
  disconnectPusher,
  NotificationEvents
};
